import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { galleryItems } from '../data/gallery';
import { company } from '../data/content';
import { ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon, ClockIcon, WaveIcon, PhoneIcon } from './icons';
const ease = [0.23, 1, 0.32, 1] as const;
export function Gallery() {
  const [index, setIndex] = useState(0);
  const active = galleryItems[index];
  const step = useCallback((direction: 1 | -1) => {
    setIndex(current => (current + direction + galleryItems.length) % galleryItems.length);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step]);
  return <section id="gallery" className="w-full border-t border-navy-700 bg-navy-800 py-20 lg:py-28" data-id="element-268" data-mp="raglir2csvb0">
      <div className="mx-auto max-w-content px-5 lg:px-8" data-id="element-269" data-mp="w9jlvy7e2dcn">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-270" data-mp="113fgqrscvk3l">
          <div className="max-w-2xl" data-id="element-271" data-mp="152wjhdgee352">
            <p className="text-sm font-semibold text-sky-300" data-id="element-272" data-mp="15629cuwup91u">Project gallery</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-273" data-mp="8f9rbrqcx6xf">
              {galleryItems.length} backyards, start to finish.
            </h2>
          </div>
          <p className="text-sm text-navy-200 sm:text-right" data-id="element-274" data-mp="hghp88oyo5qz">
            Every photo is our own crew&apos;s work.
            <br className="hidden sm:block" data-id="element-275" data-mp="1stk674uz2xbo" /> Use the arrows or pick a project below.
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,8fr)_minmax(0,4fr)]" data-id="element-276" data-mp="1xsdac6oddbz6">
          {/* Stage */}
          <div className="relative overflow-hidden rounded-lg bg-navy-900" data-id="element-277" data-mp="3tv8hmfd48fbf">
            <AnimatePresence mode="wait" initial={false} data-id="element-278" data-mp="3pu6gtu3jvv7c">
              <motion.img key={active.src} src={active.src} alt={active.alt} initial={{
              opacity: 0,
              scale: 1.02
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.28,
              ease
            }} className="h-[280px] w-full object-cover sm:h-[420px] lg:h-[520px]" data-id="element-279" data-mp="3ptmxqa25z0rq" />
            </AnimatePresence>

            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-4 sm:p-5" data-id="element-280" data-mp="3kutmype9hnv9">
              <span className="rounded-md bg-navy-900/85 px-3.5 py-2 font-display text-[13px] font-bold tabular-nums text-white" data-id="element-281" data-mp="3g3v0t96ldvk3">
                {String(index + 1).padStart(2, '0')}
                <span className="text-navy-300" data-id="element-282" data-mp="2nfz80fk58nsl"> / {String(galleryItems.length).padStart(2, '0')}</span>
              </span>
              <span className="flex gap-2" data-id="element-283" data-mp="12xew4rm7ychi">
                <button type="button" onClick={() => step(-1)} aria-label="Previous project" className="flex h-11 w-11 items-center justify-center rounded-full bg-navy-900/85 text-white transition-colors duration-150 ease-out hover:bg-navy-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300" data-id="element-284" data-mp="12vkbzfiruoeh">
                  <ChevronLeftIcon className="h-5 w-5" data-id="element-285" data-mp="1qnrd0rzfzjk8" />
                </button>
                <button type="button" onClick={() => step(1)} aria-label="Next project" className="flex h-11 w-11 items-center justify-center rounded-full bg-sky-400 text-navy-900 transition-colors duration-150 ease-out hover:bg-sky-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-200" data-id="element-286" data-mp="1cngmjq21o6zl">
                  <ChevronRightIcon className="h-5 w-5" data-id="element-287" data-mp="li2vtwl0zhr5" />
                </button>
              </span>
            </div>
          </div>

          {/* Details */}
          <div className="flex flex-col rounded-lg bg-navy-900 p-6 sm:p-7" data-id="element-288" data-mp="br8081lw35t1">
            <AnimatePresence mode="wait" initial={false} data-id="element-289" data-mp="bp5brm0dxkqf">
              <motion.div key={active.title} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.22,
              ease
            }} data-id="element-290" data-mp="7qnzyp60rm2o">
                <span className="inline-flex rounded-full bg-sky-400 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-navy-900" data-id="element-291" data-mp="veyxbicib3m7">
                  {active.category}
                </span>
                <h3 className="mt-4 font-display text-2xl font-bold leading-tight text-white" data-id="element-292" data-mp="3g56bx1z2zlrk">
                  {active.title}
                </h3>
                <p className="mt-1.5 text-sm font-semibold text-sky-200" data-id="element-293" data-mp="3b4s85w4iixzc">{active.location}</p>
                <p className="mt-4 text-[15px] leading-relaxed text-navy-100" data-id="element-294" data-mp="1qpvpt7c1992z">{active.note}</p>

                <dl className="mt-6 space-y-3 border-t border-navy-600 pt-5 text-sm" data-id="element-295" data-mp="1vnx8hr5jr6dx">
                  <div className="flex items-center gap-3" data-id="element-296" data-mp="1znxv3z2sunw2">
                    <WaveIcon className="h-4 w-4 shrink-0 text-sky-300" data-id="element-297" data-mp="24fh0zvjlck0g" />
                    <dt className="sr-only" data-id="element-298" data-mp="24httv3qw0esa">Scope</dt>
                    <dd className="text-navy-100" data-id="element-299" data-mp="29dqwy8d53on4">{active.scope}</dd>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-300" data-mp="2ed3w9sl76o6l">
                    <ClockIcon className="h-4 w-4 shrink-0 text-sky-300" data-id="element-301" data-mp="2efq3ts8393ot" />
                    <dt className="sr-only" data-id="element-302" data-mp="2jdrjs4a7wmw2">Timeline</dt>
                    <dd className="text-navy-100" data-id="element-303" data-mp="2nf39sax07a4s">{active.duration} on site</dd>
                  </div>
                </dl>
              </motion.div>
            </AnimatePresence>

            <div className="mt-auto pt-7" data-id="element-304" data-mp="t0k6w78jv4m8">
              <a href="#contact" className="flex w-full items-center justify-center gap-2 rounded-md bg-sky-400 px-5 py-3.5 font-display text-[15px] font-bold text-navy-900 transition-colors duration-150 ease-out hover:bg-sky-300" data-id="element-305" data-mp="t1bw90bk0642">
                Get this for your pool
                <ArrowRightIcon className="h-4 w-4" data-id="element-306" data-mp="25dh6ljrpjcuy" />
              </a>
              <a href={company.phoneHref} className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold text-sky-200 transition-colors duration-150 ease-out hover:text-white" data-id="element-307" data-mp="219howxuoy800">
                <PhoneIcon className="h-4 w-4" data-id="element-308" data-mp="2a94tfsipvtxa" />
                Or call {company.phoneDisplay}
              </a>
            </div>
          </div>
        </div>

        {/* Thumbnail rail */}
        <ul aria-label="Choose a project" className="mt-4 flex snap-x gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-309" data-mp="2k1u10cb5cnnc">
          {galleryItems.map((item, itemIndex) => {
          const isActive = itemIndex === index;
          return <li key={item.src} className="shrink-0 snap-start" data-id="element-310" data-mp="3clfom7u4u46x" data-mpi={itemIndex}>
                <button type="button" onClick={() => setIndex(itemIndex)} aria-current={isActive ? 'true' : undefined} aria-label={`${item.title}, ${item.location}`} className={`relative block h-20 w-28 overflow-hidden rounded-md transition-[opacity,box-shadow] duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300 sm:h-24 sm:w-36 ${isActive ? 'opacity-100 ring-2 ring-sky-400' : 'opacity-55 hover:opacity-90'}`} data-id="element-311" data-mp="3hlahui1dr5v5">
                  <img src={item.src} alt="" className="h-full w-full object-cover" data-id="element-312" data-mp="2axwtmq74rmlb" />
                </button>
              </li>;
        })}
        </ul>
      </div>
    </section>;
}